"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const validationError_1 = require("./validationError");
/**
 * Defines how to handle matching enum values from an unknown source.
 */
var EnumMatchBehavior;
(function (EnumMatchBehavior) {
    EnumMatchBehavior[EnumMatchBehavior["CaseSensitive"] = 0] = "CaseSensitive";
    EnumMatchBehavior[EnumMatchBehavior["CaseInsensitive"] = 1] = "CaseInsensitive";
})(EnumMatchBehavior = exports.EnumMatchBehavior || (exports.EnumMatchBehavior = {}));
/**
 * Helper to generate a Map of "matchable values" (based on string case sensitivity) to the original, case-sensitive values.
 * @param values The values to generate a Map from
 * @param behavior Whether to match as case-sensitive or not
 */
function buildValueLookup(values, behavior) {
    const mapping = new Map();
    for (const originalValue of values) {
        let lookupKey = originalValue;
        if (behavior === EnumMatchBehavior.CaseInsensitive) {
            lookupKey = lookupKey.toLocaleUpperCase();
        }
        mapping.set(lookupKey, originalValue);
    }
    return mapping;
}
/**
 * Given a TypeScript object representing an enum, creates a function that can be used to match
 * unknown strings against values in that enum in constant time.
 * Each validator will have some memory overhead as it uses a Map of valid values to optimize for validation speed.
 * Note - this will NOT work for const enums, as they don't exist as a dictionary to validate at runtime. Consider a string union type
 * with createStringUnionMatcher instead.
 *
 * @example
 *  // Do this once somewhere in your parsing code
 *  const getEnum = createStringEnumMatcher(MyEnumType, "MyEnumType");
 *
 *  // Validate untrusted values
 *  const validatedEnumValue = getEnum(someServiceString); // has type MyEnumType
 *  const otherValue = getEnum(someOtherString);
 *
 * @param anEnum The enum to build a matcher for
 * @param enumName Human-readable string used for errors
 * @param behavior How to handle matching enum values (defaults to case sensitive)
 * @returns A function that can be used to validate untrusted enum values
 */
function createStringEnumMatcher(anEnum, enumName, behavior = EnumMatchBehavior.CaseSensitive) {
    // Build a mapping of matchable enum value strings to their original case-sensitive values.
    const mapping = buildValueLookup(Object.values(anEnum), behavior);
    // Return a matcher using the map with correct case sensitivity.
    return function (value) {
        if (typeof value !== "string") {
            throw new validationError_1.ValidationError(`Expected ${enumName} enum value, but ${value} is not a string`, value);
        }
        const valueToMatch = behavior === EnumMatchBehavior.CaseSensitive ? value : value.toLocaleUpperCase();
        const matchedValue = mapping.get(valueToMatch);
        if (matchedValue === undefined) {
            throw new validationError_1.ValidationError(`Could not find ${value} value in enum ${enumName}`, value);
        }
        return matchedValue;
    };
}
exports.createStringEnumMatcher = createStringEnumMatcher;
/**
 * Given an array representing valid values in a string union, creates a function that can be used to match
 * unknown strings against values in that union in constant time.
 * Each validator will have some memory overhead as it uses a Map of valid values to optimize for validation speed.
 * A runtime array must be passed as an input, but the returned validator returns the desired union type, so
 * you will need a pairing of valid runtime values with the type itself, such as:
 *
 * @example
 *  const Values = ["A", "B"] as const;
 *  type MyUnion = typeof Values[number];
 *
 *  const getUnionValue = createStringUnionMatcher(Values, "Values");
 *  const validatedValue = getUnionValue(serviceString); // validatedValue has type MyUnion
 *
 * @param union The union type to build a matcher for
 * @param unionName Human-readable string used for errors
 * @param behavior How to handle matching union values (defaults to case sensitive)
 * @returns A function that can be used to validate untrusted string values
 */
function createStringUnionMatcher(union, unionName, behavior = EnumMatchBehavior.CaseSensitive) {
    // Build a mapping of matchable strings in this union to their original case-sensitive values.
    const mapping = buildValueLookup(union, behavior);
    // Return a matcher using the map with correct case sensitivity.
    return function (value) {
        if (typeof value !== "string") {
            throw new validationError_1.ValidationError(`Expected string union value from ${unionName}, but ${value} is not a string`, value);
        }
        const valueToMatch = behavior === EnumMatchBehavior.CaseSensitive ? value : value.toLocaleUpperCase();
        const matchedValue = mapping.get(valueToMatch);
        if (matchedValue === undefined) {
            throw new validationError_1.ValidationError(`Could not find ${value} value in string union ${unionName}`, value);
        }
        return matchedValue;
    };
}
exports.createStringUnionMatcher = createStringUnionMatcher;
//# sourceMappingURL=enums.js.map